import { useState, useCallback } from 'react';

import type { CreatePricingIndexPayload } from '@/api/services/settings/pricing-indices.service';

export interface UsePricingIndexFormProps {
  initialState?: Partial<CreatePricingIndexPayload>;
  onSubmit: (payload: CreatePricingIndexPayload) => Promise<void> | void;
}

export const usePricingIndexForm = ({ initialState, onSubmit }: UsePricingIndexFormProps) => {
  const defaultInitialState: CreatePricingIndexPayload = {
    name: '',
    timeLimitLower: '00:00',
    timeLimitUpper: '',
  };

  const [form, setForm] = useState<CreatePricingIndexPayload>(() => ({
    ...defaultInitialState,
    ...initialState,
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof CreatePricingIndexPayload, string>>>({});

  const updateField = useCallback(
    <K extends keyof CreatePricingIndexPayload>(field: K, value: CreatePricingIndexPayload[K]) => {
      setForm((prev) => ({ ...prev, [field]: value }));

      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [errors]
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof CreatePricingIndexPayload, string>> = {};

    if (!form.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    if (!form.timeLimitLower) {
      newErrors.timeLimitLower = 'El límite inferior es obligatorio';
    }

    if (!form.timeLimitUpper) {
      newErrors.timeLimitUpper = 'El límite superior es obligatorio';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }, [form]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    await onSubmit(form);

    setIsSubmitting(false);
  }, [validateForm, onSubmit, form]);

  return {
    form,
    isSubmitting,
    errors,
    updateField,
    handleSubmit,
  };
};

export default usePricingIndexForm;
